/**
 * Horas trabajadas y horas extraordinarias.
 *
 * Se registra la entrada, la colación y la salida; la app separa lo ordinario
 * de lo extraordinario y avisa cuando algo se sale de la ley.
 * Si no se usa, simplemente queda vacío: no bloquea nada del resto.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
} from 'recharts';
import { Clock, Plus, TriangleAlert, UserPlus, Trash2 } from 'lucide-react';
import {
  Boton,
  Campo,
  Chip,
  Indicador,
  Selector,
  Tarjeta,
  TarjetaTitulo,
  Vacio,
} from '@/components/ui';
import { actualizar, guardar, listar, suscribir } from '@/db/repositorio';
import { metaNueva, uuidv7, type Meta } from '@/db/tiposDatos';
import {
  calcularJornadaDia,
  consolidarSemanas,
  horas,
  horasSemanalesLegales,
  JORNADA_7X7,
  JORNADA_ORDINARIA,
  limiteOrdinarioDiarioMin,
  montoHorasExtra,
  type Jornada as TipoJornadaCfg,
  type JornadaDia,
} from '@/domain/jornada';
import { aFechaISO, clp, fecha as ffecha, rut as frut, rutValido } from '@/domain/formato';
import { useDialogo } from '@/components/ui/dialogo';
import { Caja, SelectorTipo, TOOLTIP, useTipoGrafico } from '@/features/dashboard/graficos';
import { cn } from '@/lib/cn';

const COL_TRABAJADORES = 'trabajadores';
const COL_MARCAS = 'marcasAsistencia';

interface DocTrabajador extends Meta {
  id: string;
  rut: string;
  nombre: string;
  cargo: string;
  jornadaId: string;
  sueldoBaseMensual: number;
  activo: boolean;
}

interface DocMarca extends Meta {
  id: string;
  trabajadorId: string;
  fecha: string;
  entrada: string | null; // HH:mm
  salida: string | null;
  inicioColacion: string | null;
  finColacion: string | null;
  observacion: string | null;
}

const JORNADAS: TipoJornadaCfg[] = [JORNADA_ORDINARIA, JORNADA_7X7];

/** 'HH:mm' del día `fecha` → instante. Si la salida retrocede, cruzó medianoche. */
function instante(fecha: string, hhmm: string | null, previo?: Date | null): Date | null {
  if (!hhmm) return null;
  const [hh, mm] = hhmm.split(':').map(Number);
  const [a, me, d] = fecha.split('-').map(Number);
  const i = new Date(a!, me! - 1, d!, hh ?? 0, mm ?? 0, 0, 0);
  if (previo && i < previo) i.setDate(i.getDate() + 1);
  return i;
}

function inicioDeMes(): string {
  const d = new Date();
  return aFechaISO(new Date(d.getFullYear(), d.getMonth(), 1));
}

export default function Jornada() {
  const [trabajadores, setTrabajadores] = useState<DocTrabajador[]>([]);
  const [marcas, setMarcas] = useState<DocMarca[]>([]);
  const [desde, setDesde] = useState(inicioDeMes());
  const [hasta, setHasta] = useState(aFechaISO(new Date()));
  const [filtroTrabajador, setFiltroTrabajador] = useState('');
  const [nuevoAbierto, setNuevoAbierto] = useState(false);
  const { confirmar } = useDialogo();
  const [tipoJornada, setTipoJornada] = useTipoGrafico('jornadaDiaria', 'puntos', [
    'puntos',
    'barrasH',
    'barras',
  ]);

  const cargar = async () => {
    const [t, m] = await Promise.all([
      listar<DocTrabajador>(COL_TRABAJADORES),
      listar<DocMarca>(COL_MARCAS),
    ]);
    setTrabajadores(
      t.filter((x) => !x.eliminadoEn).sort((a, b) => a.nombre.localeCompare(b.nombre, 'es')),
    );
    setMarcas(m.filter((x) => !x.eliminadoEn));
  };

  useEffect(() => {
    void cargar();
    const off = [COL_TRABAJADORES, COL_MARCAS].map((c) => suscribir(c, () => void cargar()));
    return () => off.forEach((f) => f());
  }, []);

  const jornadaDe = (t: DocTrabajador) =>
    JORNADAS.find((j) => j.id === t.jornadaId) ?? JORNADA_ORDINARIA;

  // ── Cálculo ─────────────────────────────────────────────────────────────
  const dias: (JornadaDia & {
    nombre: string;
    cargo: string;
    sueldo: number;
    jornada: TipoJornadaCfg;
  })[] = useMemo(() => {
    return marcas
      .filter((m) => m.fecha >= desde && m.fecha <= hasta)
      .filter((m) => !filtroTrabajador || m.trabajadorId === filtroTrabajador)
      .map((m) => {
        const t = trabajadores.find((x) => x.id === m.trabajadorId);
        if (!t) return null;
        const j = jornadaDe(t);
        const entrada = instante(m.fecha, m.entrada);
        const iniCol = instante(m.fecha, m.inicioColacion, entrada);
        const finCol = instante(m.fecha, m.finColacion, iniCol);
        const salida = instante(m.fecha, m.salida, finCol ?? entrada);
        const d = calcularJornadaDia(
          {
            trabajadorId: t.id,
            fecha: m.fecha,
            entrada,
            salida,
            inicioColacion: iniCol,
            finColacion: finCol,
          },
          { jornada: j },
        );
        return { ...d, nombre: t.nombre, cargo: t.cargo, sueldo: t.sueldoBaseMensual, jornada: j };
      })
      .filter((x): x is NonNullable<typeof x> => x !== null)
      .sort((a, b) => a.fecha.localeCompare(b.fecha));
  }, [marcas, trabajadores, desde, hasta, filtroTrabajador]);

  const k = useMemo(() => {
    const ordinarios = dias.reduce((s, d) => s + (d.minutosOrdinarios ?? 0), 0);
    const extra = dias.reduce((s, d) => s + (d.minutosExtra ?? 0), 0);
    const monto = dias.reduce(
      (s, d) => s + montoHorasExtra(d.minutosExtra ?? 0, d.sueldo, d.jornada),
      0,
    );
    return {
      ordinarios,
      extra,
      monto,
      alertas: dias.filter((d) => d.alertas.length > 0).length,
      incompletas: dias.filter((d) => d.incompleta).length,
    };
  }, [dias]);

  const semanas = useMemo(() => {
    const porTrabajador = new Map<string, JornadaDia[]>();
    for (const d of dias)
      porTrabajador.set(d.trabajadorId, [...(porTrabajador.get(d.trabajadorId) ?? []), d]);
    return [...porTrabajador.entries()].flatMap(([id, ds]) => {
      const t = trabajadores.find((x) => x.id === id);
      return consolidarSemanas(ds, t ? jornadaDe(t) : JORNADA_ORDINARIA).map((s) => ({
        ...s,
        nombre: t?.nombre ?? '—',
      }));
    });
  }, [dias, trabajadores]);

  /** Puntos del gráfico: un punto por trabajador y día. */
  const puntos = useMemo(
    () =>
      dias
        .filter((d) => d.minutosTrabajados !== null)
        .map((d) => ({
          x: new Date(d.fecha + 'T12:00:00').getTime(),
          y: Number(((d.minutosTrabajados ?? 0) / 60).toFixed(2)),
          extra: Number(((d.minutosExtra ?? 0) / 60).toFixed(2)),
          nombre: d.nombre,
          fecha: d.fecha,
          conAlerta: d.alertas.length > 0,
        })),
    [dias],
  );

  const limiteHoras = limiteOrdinarioDiarioMin(JORNADA_ORDINARIA) / 60;

  /** Horas trabajadas por trabajador en el período, para las vistas de barras. */
  const porTrabajador = useMemo(() => {
    const m = new Map<string, { ordinarias: number; extra: number }>();
    for (const d of dias) {
      const a = m.get(d.nombre) ?? { ordinarias: 0, extra: 0 };
      m.set(d.nombre, {
        ordinarias: a.ordinarias + (d.minutosOrdinarios ?? 0) / 60,
        extra: a.extra + (d.minutosExtra ?? 0) / 60,
      });
    }
    return [...m.entries()]
      .map(([nombre, v]) => ({
        nombre,
        ordinarias: Number(v.ordinarias.toFixed(1)),
        extra: Number(v.extra.toFixed(1)),
        total: Number((v.ordinarias + v.extra).toFixed(1)),
      }))
      .sort((a, b) => b.ordinarias + b.extra - (a.ordinarias + a.extra));
  }, [dias]);

  // ── Alta de trabajador ──────────────────────────────────────────────────
  const [nt, setNt] = useState({
    rut: '',
    nombre: '',
    cargo: '',
    jornadaId: JORNADA_ORDINARIA.id,
    sueldo: '',
  });

  async function crearTrabajador() {
    if (!nt.nombre.trim()) return;
    const doc: DocTrabajador = {
      ...metaNueva(null),
      id: uuidv7(),
      rut: nt.rut.trim(),
      nombre: nt.nombre.trim(),
      cargo: nt.cargo.trim(),
      jornadaId: nt.jornadaId,
      sueldoBaseMensual: Number(nt.sueldo.replace(/\./g, '').replace(',', '.')) || 0,
      activo: true,
    };
    await guardar(COL_TRABAJADORES, doc);
    setNt({ rut: '', nombre: '', cargo: '', jornadaId: JORNADA_ORDINARIA.id, sueldo: '' });
    setNuevoAbierto(false);
    void cargar();
  }

  // ── Registro de marca ───────────────────────────────────────────────────
  const [nm, setNm] = useState({
    trabajadorId: '',
    fecha: aFechaISO(new Date()),
    entrada: '',
    inicioColacion: '',
    finColacion: '',
    salida: '',
  });

  async function registrarMarca() {
    if (!nm.trabajadorId || !nm.entrada) return;
    const existente = marcas.find(
      (m) => m.trabajadorId === nm.trabajadorId && m.fecha === nm.fecha,
    );
    const doc: DocMarca = {
      ...(existente ?? metaNueva(null)),
      id: existente?.id ?? uuidv7(),
      trabajadorId: nm.trabajadorId,
      fecha: nm.fecha,
      entrada: nm.entrada || null,
      salida: nm.salida || null,
      inicioColacion: nm.inicioColacion || null,
      finColacion: nm.finColacion || null,
      observacion: null,
    };
    await guardar(COL_MARCAS, doc);
    setNm((p) => ({ ...p, entrada: '', inicioColacion: '', finColacion: '', salida: '' }));
    void cargar();
  }

  const ahora = () =>
    `${String(new Date().getHours()).padStart(2, '0')}:${String(new Date().getMinutes()).padStart(2, '0')}`;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold">Horas trabajadas</h1>
          <p className="text-xs text-texto-mute">
            Jornada ordinaria vigente: {horasSemanalesLegales(aFechaISO(new Date()))} h semanales ·
            recargo hora extra 50%
          </p>
        </div>
        <Boton
          variante="secundario"
          icono={<UserPlus size={15} />}
          onClick={() => setNuevoAbierto((v) => !v)}
        >
          Agregar trabajador
        </Boton>
      </div>

      {trabajadores.length === 0 && !nuevoAbierto && (
        <Tarjeta>
          <Vacio icono={<Clock size={30} />} titulo="Todavía no hay trabajadores registrados">
            Agrega el primero para empezar a llevar la jornada. Si por ahora no se va a usar, esta
            sección simplemente queda vacía y no afecta al resto de la app.
          </Vacio>
        </Tarjeta>
      )}

      {nuevoAbierto && (
        <Tarjeta>
          <TarjetaTitulo>Nuevo trabajador</TarjetaTitulo>
          <div className="grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-5">
            <Campo
              etiqueta="RUT"
              name="ntRut"
              value={nt.rut}
              onChange={(e) => setNt({ ...nt, rut: e.target.value })}
              error={nt.rut && !rutValido(nt.rut) ? 'RUT inválido' : undefined}
              className="font-mono"
            />
            <Campo
              etiqueta="Nombre"
              name="ntNombre"
              value={nt.nombre}
              onChange={(e) => setNt({ ...nt, nombre: e.target.value })}
              className="lg:col-span-2"
            />
            <Campo
              etiqueta="Cargo"
              name="ntCargo"
              value={nt.cargo}
              onChange={(e) => setNt({ ...nt, cargo: e.target.value })}
            />
            <Selector
              etiqueta="Jornada"
              name="ntJornada"
              value={nt.jornadaId}
              onChange={(e) => setNt({ ...nt, jornadaId: e.target.value })}
            >
              {JORNADAS.map((j) => (
                <option key={j.id} value={j.id}>
                  {j.nombre}
                </option>
              ))}
            </Selector>
            <Campo
              etiqueta="Sueldo base mensual"
              name="ntSueldo"
              inputMode="numeric"
              sufijo="$"
              value={nt.sueldo}
              onChange={(e) => setNt({ ...nt, sueldo: e.target.value })}
              className="font-mono"
            />
            <div className="flex items-end gap-2 lg:col-span-2">
              <Boton variante="primario" onClick={() => void crearTrabajador()}>
                Guardar
              </Boton>
              <Boton variante="fantasma" onClick={() => setNuevoAbierto(false)}>
                Cancelar
              </Boton>
            </div>
          </div>
        </Tarjeta>
      )}

      {trabajadores.length > 0 && (
        <>
          <Tarjeta>
            <TarjetaTitulo>Registrar jornada del día</TarjetaTitulo>
            <div className="grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-6">
              <Selector
                etiqueta="Trabajador"
                name="nmTrabajador"
                value={nm.trabajadorId}
                onChange={(e) => setNm({ ...nm, trabajadorId: e.target.value })}
                className="lg:col-span-2"
              >
                <option value="">Seleccionar…</option>
                {trabajadores.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.nombre}
                  </option>
                ))}
              </Selector>
              <Campo
                etiqueta="Fecha"
                name="nmFecha"
                type="date"
                value={nm.fecha}
                onChange={(e) => setNm({ ...nm, fecha: e.target.value })}
              />
              {(
                [
                  ['entrada', 'Entrada'],
                  ['inicioColacion', 'Inicio colación'],
                  ['finColacion', 'Fin colación'],
                  ['salida', 'Salida'],
                ] as const
              ).map(([clave, etiqueta]) => (
                <div key={clave}>
                  <span className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-texto-mute">
                    {etiqueta}
                  </span>
                  <div className="flex gap-1.5">
                    <input
                      type="time"
                      name={clave}
                      value={nm[clave]}
                      onChange={(e) => setNm({ ...nm, [clave]: e.target.value })}
                      className="min-h-tactil w-full rounded-control border border-linea bg-petroleo-800/60 px-2 font-mono text-sm text-texto focus:border-petroleo-300 focus:outline-none"
                    />
                    <button
                      type="button"
                      onClick={() => setNm({ ...nm, [clave]: ahora() })}
                      className="min-h-tactil shrink-0 rounded-control bg-petroleo-500 px-2.5 text-xs font-semibold text-white hover:bg-petroleo-400 active:scale-95"
                    >
                      Ahora
                    </button>
                  </div>
                </div>
              ))}
              <div className="flex items-end lg:col-span-6">
                <Boton
                  variante="primario"
                  icono={<Plus size={15} />}
                  onClick={() => void registrarMarca()}
                  disabled={!nm.trabajadorId || !nm.entrada}
                >
                  Registrar jornada
                </Boton>
              </div>
            </div>
          </Tarjeta>

          <Tarjeta>
            <div className="grid gap-3 p-4 sm:grid-cols-3">
              <Campo
                etiqueta="Desde"
                name="fDesde"
                type="date"
                value={desde}
                onChange={(e) => setDesde(e.target.value)}
              />
              <Campo
                etiqueta="Hasta"
                name="fHasta"
                type="date"
                value={hasta}
                onChange={(e) => setHasta(e.target.value)}
              />
              <Selector
                etiqueta="Trabajador"
                name="fTrabajador"
                value={filtroTrabajador}
                onChange={(e) => setFiltroTrabajador(e.target.value)}
              >
                <option value="">Todos</option>
                {trabajadores.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.nombre}
                  </option>
                ))}
              </Selector>
            </div>
          </Tarjeta>

          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Indicador etiqueta="Horas ordinarias" valor={horas(k.ordinarios)} />
            <Indicador
              etiqueta="Horas extraordinarias"
              valor={horas(k.extra)}
              nivel={k.extra > 0 ? 'AMARILLO' : 'NEUTRO'}
              detalle="con recargo del 50%"
            />
            <Indicador
              etiqueta="Costo horas extra"
              valor={clp(k.monto)}
              detalle="estimado sobre sueldo base"
            />
            <Indicador
              etiqueta="Días con alerta"
              valor={k.alertas}
              nivel={k.alertas > 0 ? 'ROJO' : 'VERDE'}
              detalle={
                k.alertas > 0
                  ? k.incompletas > 0
                    ? `incluye ${k.incompletas} sin cerrar`
                    : 'revisar el detalle'
                  : 'todo en regla'
              }
            />
          </div>

          {/* ── Gráfico de puntos: dispersión de la jornada ─────────────── */}
          <Tarjeta>
            <TarjetaTitulo
              accion={
                <div className="flex items-center gap-3">
                  {tipoJornada === 'puntos' ? (
                    <div className="hidden gap-3 text-[11px] text-texto-mute sm:flex">
                      <span className="flex items-center gap-1">
                        <span className="h-2 w-2 rounded-full bg-petroleo-300" /> normal
                      </span>
                      <span className="flex items-center gap-1">
                        <span className="h-2 w-2 rounded-full bg-fluor-amarillo" /> con extras
                      </span>
                      <span className="flex items-center gap-1">
                        <span className="h-2 w-2 rounded-full bg-fluor-rojo" /> fuera de norma
                      </span>
                    </div>
                  ) : (
                    <div className="hidden gap-3 text-[11px] text-texto-mute sm:flex">
                      <span className="flex items-center gap-1">
                        <span className="h-2 w-2 rounded-full bg-petroleo-300" /> ordinarias
                      </span>
                      <span className="flex items-center gap-1">
                        <span className="h-2 w-2 rounded-full bg-fluor-amarillo" /> extraordinarias
                      </span>
                    </div>
                  )}
                  <SelectorTipo
                    tipo={tipoJornada}
                    alCambiar={setTipoJornada}
                    disponibles={['puntos', 'barrasH', 'barras']}
                  />
                </div>
              }
            >
              {tipoJornada === 'puntos'
                ? 'Jornada diaria por trabajador'
                : 'Horas del período por trabajador'}
            </TarjetaTitulo>
            <div
              className="p-4"
              style={{
                height:
                  tipoJornada === 'barrasH' ? Math.max(220, porTrabajador.length * 40 + 60) : 288,
              }}
            >
              {puntos.length === 0 ? (
                <Vacio titulo="Sin jornadas registradas en el período" />
              ) : tipoJornada !== 'puntos' ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={porTrabajador}
                    layout={tipoJornada === 'barrasH' ? 'vertical' : 'horizontal'}
                    margin={{
                      top: 6,
                      right: 28,
                      bottom: 2,
                      left: tipoJornada === 'barrasH' ? 0 : -14,
                    }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="rgba(255,255,255,.07)"
                      horizontal={tipoJornada !== 'barrasH'}
                      vertical={tipoJornada === 'barrasH'}
                    />
                    {tipoJornada === 'barrasH' ? (
                      <>
                        <XAxis
                          type="number"
                          tick={{ fill: '#8FB3BD', fontSize: 11 }}
                          axisLine={false}
                          tickLine={false}
                          tickFormatter={(v: number) => `${v} h`}
                        />
                        <YAxis
                          type="category"
                          dataKey="nombre"
                          tick={{ fill: '#C9E5ED', fontSize: 11 }}
                          axisLine={false}
                          tickLine={false}
                          width={150}
                        />
                      </>
                    ) : (
                      <>
                        <XAxis
                          dataKey="nombre"
                          tick={{ fill: '#8FB3BD', fontSize: 11 }}
                          axisLine={false}
                          tickLine={false}
                          interval={0}
                        />
                        <YAxis
                          tick={{ fill: '#8FB3BD', fontSize: 11 }}
                          axisLine={false}
                          tickLine={false}
                          tickFormatter={(v: number) => `${v} h`}
                        />
                      </>
                    )}
                    <Tooltip
                      {...TOOLTIP}
                      content={({ payload }) => {
                        const t = payload?.[0]?.payload as
                          { nombre: string; ordinarias: number; extra: number } | undefined;
                        if (!t) return null;
                        return (
                          <Caja
                            titulo={t.nombre}
                            filas={[
                              ['Ordinarias', `${t.ordinarias} h`],
                              ['Extraordinarias', `${t.extra} h`],
                              ['Total', `${(t.ordinarias + t.extra).toFixed(1)} h`],
                            ]}
                          />
                        );
                      }}
                    />
                    {/* Apiladas: la extra se lee sobre la ordinaria, no aparte. */}
                    <Bar
                      dataKey="ordinarias"
                      stackId="j"
                      fill="#4BA3B8"
                      radius={tipoJornada === 'barrasH' ? [0, 0, 0, 0] : [0, 0, 0, 0]}
                      barSize={tipoJornada === 'barrasH' ? 18 : undefined}
                      maxBarSize={54}
                    />
                    <Bar
                      dataKey="extra"
                      stackId="j"
                      fill="#EAFF00"
                      radius={tipoJornada === 'barrasH' ? [0, 4, 4, 0] : [4, 4, 0, 0]}
                      barSize={tipoJornada === 'barrasH' ? 18 : undefined}
                      maxBarSize={54}
                    >
                      <LabelList
                        dataKey="total"
                        position={tipoJornada === 'barrasH' ? 'right' : 'top'}
                        formatter={(v: number) => `${v} h`}
                        style={{
                          fill: '#8FB3BD',
                          fontSize: 10,
                          fontFamily: 'JetBrains Mono, monospace',
                        }}
                      />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <ScatterChart margin={{ top: 8, right: 12, bottom: 4, left: -18 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,.07)" />
                    <XAxis
                      type="number"
                      dataKey="x"
                      domain={['dataMin - 43200000', 'dataMax + 43200000']}
                      tickFormatter={(v: number) => {
                        const d = new Date(v);
                        return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
                      }}
                      tick={{ fill: '#8FB3BD', fontSize: 11 }}
                      axisLine={{ stroke: 'rgba(255,255,255,.1)' }}
                      tickLine={false}
                    />
                    <YAxis
                      type="number"
                      dataKey="y"
                      name="Horas"
                      unit=" h"
                      domain={[0, (max: number) => Math.max(12, Math.ceil(max) + 1)]}
                      tick={{ fill: '#8FB3BD', fontSize: 11 }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <ZAxis range={[70, 70]} />

                    {/* Límite de jornada ordinaria y tope legal diario */}
                    <ReferenceLine
                      y={limiteHoras}
                      stroke="#4BA3B8"
                      strokeDasharray="4 4"
                      label={{
                        value: `ordinaria ${horas(limiteHoras * 60)}`,
                        fill: '#8FB3BD',
                        fontSize: 10,
                        position: 'insideBottomRight',
                      }}
                    />
                    <ReferenceLine
                      y={10}
                      stroke="#FF2D55"
                      strokeDasharray="4 4"
                      label={{
                        value: 'máximo legal 10 h',
                        fill: '#FF2D55',
                        fontSize: 10,
                        position: 'insideTopRight',
                      }}
                    />

                    <Tooltip
                      cursor={{ strokeDasharray: '3 3', stroke: 'rgba(255,255,255,.2)' }}
                      contentStyle={{
                        background: '#0E3A46',
                        border: '1px solid rgba(255,255,255,.12)',
                        borderRadius: 10,
                        fontSize: 12,
                      }}
                      formatter={(v: number, n: string) =>
                        n === 'Horas' ? [`${v} h`, 'Trabajadas'] : [v, n]
                      }
                      labelFormatter={() => ''}
                      content={({ payload }) => {
                        const p = payload?.[0]?.payload as (typeof puntos)[number] | undefined;
                        if (!p) return null;
                        return (
                          <div className="rounded-[10px] border border-white/10 bg-petroleo-700 px-3 py-2 text-xs">
                            <div className="font-semibold">{p.nombre}</div>
                            <div className="text-texto-mute">{ffecha(p.fecha)}</div>
                            <div className="mt-1 font-mono">
                              {p.y} h trabajadas
                              {p.extra > 0 && (
                                <span className="text-fluor-amarillo"> · {p.extra} h extra</span>
                              )}
                            </div>
                          </div>
                        );
                      }}
                    />

                    <Scatter data={puntos} shape="circle">
                      {puntos.map((p, i) => (
                        <Cell
                          key={i}
                          fill={p.conAlerta ? '#FF2D55' : p.extra > 0 ? '#EAFF00' : '#4BA3B8'}
                          fillOpacity={0.85}
                        />
                      ))}
                    </Scatter>
                  </ScatterChart>
                </ResponsiveContainer>
              )}
            </div>
          </Tarjeta>

          {/* ── Alertas de cumplimiento ─────────────────────────────────── */}
          {dias.some((d) => d.alertas.length > 0) && (
            <Tarjeta>
              <TarjetaTitulo>Alertas de cumplimiento</TarjetaTitulo>
              <div className="divide-y divide-linea/50">
                {dias
                  .filter((d) => d.alertas.length > 0)
                  .map((d) => (
                    <div key={d.trabajadorId + d.fecha} className="px-4 py-2.5">
                      <div className="flex items-center gap-2 text-xs">
                        <span className="font-medium">{d.nombre}</span>
                        <span className="text-texto-mute">{ffecha(d.fecha)}</span>
                      </div>
                      {d.alertas.map((a) => (
                        <div
                          key={a}
                          className="mt-1 flex items-start gap-2 text-xs text-fluor-rojo"
                        >
                          <TriangleAlert size={13} className="mt-0.5 shrink-0" />
                          {a}
                        </div>
                      ))}
                    </div>
                  ))}
              </div>
            </Tarjeta>
          )}

          {/* ── Detalle ─────────────────────────────────────────────────── */}
          <div className="grid gap-4 lg:grid-cols-2">
            <Tarjeta>
              <TarjetaTitulo>Detalle diario</TarjetaTitulo>
              {dias.length === 0 ? (
                <Vacio titulo="Sin registros en el período" />
              ) : (
                <div className="max-h-96 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-petroleo-700">
                      <tr className="text-left text-[10px] uppercase tracking-wide text-texto-mute">
                        <th className="px-4 py-2 font-medium">Fecha</th>
                        <th className="px-3 py-2 font-medium">Trabajador</th>
                        <th className="px-3 py-2 text-right font-medium">Trabajadas</th>
                        <th className="px-3 py-2 text-right font-medium">Extras</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dias.map((d) => (
                        <tr key={d.trabajadorId + d.fecha} className="border-t border-linea/40">
                          <td className="px-4 py-1.5">{ffecha(d.fecha)}</td>
                          <td className="max-w-[10rem] truncate px-3 py-1.5">{d.nombre}</td>
                          <td className="px-3 py-1.5 text-right font-mono tabular-nums">
                            {horas(d.minutosTrabajados)}
                          </td>
                          <td
                            className={cn(
                              'px-3 py-1.5 text-right font-mono tabular-nums',
                              (d.minutosExtra ?? 0) > 0 && 'text-fluor-amarillo',
                            )}
                          >
                            {horas(d.minutosExtra)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Tarjeta>

            <Tarjeta>
              <TarjetaTitulo>Resumen semanal</TarjetaTitulo>
              {semanas.length === 0 ? (
                <Vacio titulo="Sin semanas completas" />
              ) : (
                <div className="divide-y divide-linea/50">
                  {semanas.map((s) => (
                    <div key={s.trabajadorId + s.semana} className="px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        <span className="min-w-0 flex-1 truncate text-sm">{s.nombre}</span>
                        <Chip nivel={s.excedeJornadaSemanal ? 'AMARILLO' : 'VERDE'}>
                          {horas(s.minutosOrdinarios)} ord.
                        </Chip>
                        {s.minutosExtra > 0 && (
                          <Chip nivel="AMARILLO">{horas(s.minutosExtra)} extra</Chip>
                        )}
                      </div>
                      <div className="mt-0.5 text-[11px] text-texto-mute">
                        Semana del {ffecha(s.semana)} · {s.diasTrabajados} días
                      </div>
                      {s.alertas.map((a) => (
                        <div key={a} className="mt-1 text-[11px] text-fluor-amarillo">
                          {a}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </Tarjeta>
          </div>

          <Tarjeta>
            <TarjetaTitulo>Trabajadores</TarjetaTitulo>
            <div className="divide-y divide-linea/50">
              {trabajadores.map((t) => (
                <div key={t.id} className="flex items-center gap-3 px-4 py-2.5 text-sm">
                  <div className="min-w-0 flex-1">
                    <div className="truncate">{t.nombre}</div>
                    <div className="text-[11px] text-texto-mute">
                      {t.rut ? frut(t.rut) + ' · ' : ''}
                      {t.cargo || 'sin cargo'} · {jornadaDe(t).nombre}
                    </div>
                  </div>
                  <span className="shrink-0 font-mono text-xs tabular-nums text-texto-mute">
                    {t.sueldoBaseMensual ? clp(t.sueldoBaseMensual) : '—'}
                  </span>
                  <Boton
                    variante="fantasma"
                    tamano="sm"
                    icono={<Trash2 size={13} />}
                    onClick={() => {
                      void confirmar({
                        titulo: `Quitar a ${t.nombre}`,
                        mensaje:
                          'Deja de aparecer en el registro de jornadas. Las horas ya cargadas se conservan.',
                        aceptar: 'Quitar',
                        tono: 'peligro',
                      }).then((si) => {
                        if (si)
                          void actualizar(
                            COL_TRABAJADORES,
                            t.id,
                            { eliminadoEn: new Date().toISOString() },
                            null,
                          ).then(cargar);
                      });
                    }}
                  />
                </div>
              ))}
            </div>
          </Tarjeta>
        </>
      )}

      <p className="px-1 text-[11px] leading-relaxed text-texto-mute">
        Los parámetros legales (jornada semanal, topes diarios y recargo) siguen la Ley 21.561 y el
        Código del Trabajo, y son configurables. Antes de usar estos montos en nómina conviene que
        los valide el área laboral, sobre todo el sistema excepcional de turnos, que depende de la
        resolución que la Dirección del Trabajo le haya autorizado a la empresa.
      </p>
    </div>
  );
}
